import React, { useState } from "react";
import { Button, Divider, Empty, Select, Typography } from "antd";
import { ApiKey } from "../../../../Entities/ApiKey";
import { openNotification } from "../../../../Components/Notification";
import { CopyOutlined } from "@ant-design/icons";
import "./index.scss";

interface WebProps {
  apiKeys: ApiKey[];
  setSubTab: React.Dispatch<React.SetStateAction<string>>;
}

const Web: React.FC<WebProps> = ({ apiKeys, setSubTab }) => {
  const activeApiKeyIds = apiKeys
    .filter((apiKey) => apiKey.status === "active")
    .map((apiKey) => apiKey.apiKey);

  const [selectedApiKeyId, setSelectedApiKeyId] = useState<string | null>(null);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);

  const constructIframeUrl = (apiKeyId: string) => {
    return `${process.env.REACT_APP_API_BASE_URL}/integration/iframe/init?apiKey=${apiKeyId}`;
  };

  const handleApiKeyIdSelection = (id: string) => {
    if (!id) {
      setSelectedApiKeyId(null);
      setIframeUrl(null);
      return;
    }

    if (id === selectedApiKeyId) {
      return;
    }

    setSelectedApiKeyId(id);
    setIframeUrl(null);
    const iframeUrl = constructIframeUrl(id);
    setIframeUrl(iframeUrl);
  };

  const handleCopyToClipboard = async () => {
    if (!iframeUrl) {
      openNotification("error", "Error", "No iframe URL available to copy");
      return;
    }

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(iframeUrl);
        openNotification(
          "success",
          "Copied",
          "Integration iframe url copied to clipboard"
        );
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = iframeUrl;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          const successful = document.execCommand("copy");
          if (successful) {
            openNotification(
              "success",
              "Copied",
              "Integration iframe url copied to clipboard"
            );
          } else {
            throw new Error("Copy command failed");
          }
        } catch (err) {
          throw new Error("Copy command failed");
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      openNotification(
        "error",
        "Failed to copy integration iframe url",
        "Please copy the iframe url manually from the code block"
      );
    }
  };

  return (
    <section className="integration-tab-content-section">
      <div className="integration-details-section-header">
        <Typography.Title level={5} style={{ margin: 0 }}>
          Web Integration
        </Typography.Title>
        <Divider style={{ margin: "0" }} />
      </div>

      <section className="integration-tab-content-section-content">
        <Typography.Paragraph>
          <strong>QualifyMe</strong> Web integration allows you to embed
          QualifyMe's job listings and application forms directly on your
          website.
        </Typography.Paragraph>

        <Typography.Title level={5} style={{ margin: 0 }}>
          Embed QualifyMe
        </Typography.Title>

        {activeApiKeyIds.length > 0 && (
          <div className="integration-script-container">
            <Typography.Paragraph>
              Please select an API key to generate the iframe url.
            </Typography.Paragraph>

            <Select
              value={selectedApiKeyId}
              options={activeApiKeyIds.map((id) => ({
                label: id,
                value: id,
              }))}
              onChange={(value) => {
                if (value) {
                  handleApiKeyIdSelection(value);
                } else {
                  setSelectedApiKeyId(null);
                  setIframeUrl(null);
                }
              }}
              allowClear
              placeholder="Select API key"
              style={{ width: "100%", maxWidth: "400px" }}
            />

            {iframeUrl && <Divider style={{ margin: "0" }} />}

            {iframeUrl && (
              <Typography.Text>
                Copy the following URL to a iframe tag on your website to embed
                the QualifyMe Candidate Portal.
              </Typography.Text>
            )}

            {iframeUrl && (
              <div className="code-container">
                <div className="code-header">
                  <span className="code-label">Url:</span>
                  <Button
                    type="primary"
                    onClick={handleCopyToClipboard}
                    icon={<CopyOutlined />}
                  >
                    Copy
                  </Button>
                </div>

                <div className="code-body">
                  <pre id="code">
                    <code>{iframeUrl}</code>
                  </pre>
                </div>
              </div>
            )}
          </div>
        )}

        {activeApiKeyIds.length === 0 && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span>
                {`No active API keys found. `}
                <Typography.Link
                  onClick={() => setSubTab("api-keys")}
                  style={{ cursor: "pointer" }}
                >
                  Click here to create or activate an API key
                </Typography.Link>
                {` to enable web integration.`}
              </span>
            }
          />
        )}
      </section>
    </section>
  );
};

export default Web;
